/** The seven days in the order the week is drawn, Monday first. */
export const PAINTED_WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type PaintedWeekday = (typeof PAINTED_WEEKDAYS)[number];

/** One open window: `open` and `close` as `HH:MM`, `close` exclusive, `24:00`
 *  for a window that runs to midnight. The messenger's stored shape. */
export type PaintedWindow = { close: string; days: PaintedWeekday[]; open: string };

/** Half-hour cells in a day. */
export const SLOTS = 48;

export type PaintedCells = Record<PaintedWeekday, boolean[]>;

const emptyRow = (): boolean[] => Array.from({ length: SLOTS }, () => false);

export const emptyCells = (): PaintedCells => ({ fri: emptyRow(), mon: emptyRow(), sat: emptyRow(), sun: emptyRow(), thu: emptyRow(), tue: emptyRow(), wed: emptyRow() });

/** `HH:MM` to the half-hour slot it starts, 0 to 48; a minute inside a half
 *  hour rounds down to it. `00:00` read as a close means midnight, 48. */
export const slotOf = (hhmm: string, asClose = false): number => {
  const [hours = 0, minutes = 0] = hhmm.split(':').map(part => Number.parseInt(part, 10));
  const slot = Math.min(SLOTS, Math.max(0, hours * 2 + (minutes >= 30 ? 1 : 0)));
  return asClose && slot === 0 ? SLOTS : slot;
};

/** A slot back to `HH:MM`; 48 is `24:00`. */
export const timeOf = (slot: number): string => `${String(Math.floor(slot / 2)).padStart(2, '0')}:${slot % 2 === 0 ? '00' : '30'}`;

/** The windows painted onto the grid: every day a window names takes its
 *  half hours. Windows may overlap; a cell is painted once. */
export const paintedCells = (windows: PaintedWindow[]): PaintedCells => {
  const cells = emptyCells();
  for (const window of windows) {
    const from = slotOf(window.open);
    const to = slotOf(window.close, true);
    for (const day of window.days) {
      for (let slot = from; slot < to; slot += 1) cells[day][slot] = true;
    }
  }
  return cells;
};

export type PaintedRun = { end: number; start: number };

/** The painted runs of one row, `end` exclusive, in order. */
export const runsOf = (row: boolean[]): PaintedRun[] => {
  const runs: PaintedRun[] = [];
  let start: number | null = null;
  row.forEach((painted, slot) => {
    if (painted && start === null) start = slot;
    if (!painted && start !== null) {
      runs.push({ end: slot, start });
      start = null;
    }
  });
  if (start !== null) runs.push({ end: row.length, start });
  return runs;
};

/** The grid back to windows: one window per painted run, each naming its one
 *  day, days in the week's order and runs in the day's. Adjacent cells merge
 *  into one run, a gap splits two, so the stored list is exactly the picture. */
export const windowsOf = (cells: PaintedCells): PaintedWindow[] => PAINTED_WEEKDAYS.flatMap(day => runsOf(cells[day]).map(run => ({ close: timeOf(run.end), days: [day], open: timeOf(run.start) })));

/** The cells with a rectangle of the grid set to `painted`, corners inclusive
 *  and in either order: what one stroke of the pointer, or shift with an
 *  arrow, does to the picture. */
export const withRectangle = (cells: PaintedCells, from: { day: PaintedWeekday; slot: number }, to: { day: PaintedWeekday; slot: number }, painted: boolean): PaintedCells => {
  const dayFrom = Math.min(PAINTED_WEEKDAYS.indexOf(from.day), PAINTED_WEEKDAYS.indexOf(to.day));
  const dayTo = Math.max(PAINTED_WEEKDAYS.indexOf(from.day), PAINTED_WEEKDAYS.indexOf(to.day));
  const slotFrom = Math.min(from.slot, to.slot);
  const slotTo = Math.max(from.slot, to.slot);
  const next = { ...cells };
  for (let dayIndex = dayFrom; dayIndex <= dayTo; dayIndex += 1) {
    const day = PAINTED_WEEKDAYS[dayIndex] as PaintedWeekday;
    const row = [...next[day]];
    for (let slot = slotFrom; slot <= slotTo; slot += 1) row[slot] = painted;
    next[day] = row;
  }
  return next;
};
